import { CacheProvider, EmotionCache } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-license-pro/utils";
import { FC, ReactNode } from "react";

import { Rtl } from "~ui-components/components/atoms/rtl";
import {
	SettingsConsumer,
	SettingsProvider,
} from "~ui-components/contexts/settings";
import { LocalizationProvider } from "~ui-components/providers/localization-provider";
import { createTheme } from "~ui-components/theme/create-theme";

LicenseInfo.setLicenseKey(
	"54b15a57982fcfd357f997b69b2e34f7T1JERVI6Mzk0NzQsRVhQSVJZPTE2Nzg2MTEzNDAwMDAsS0VZVkVSU0lPTj0x",
);

export interface UiProviderProps {
	emotionCache: EmotionCache;
	children: ReactNode;
}
export const UiProvider: FC<UiProviderProps> = ({ emotionCache, children }) => (
	<CacheProvider value={emotionCache}>
		<LocalizationProvider>
			<SettingsProvider>
				<SettingsConsumer>
					{(settings) => {
						const theme = createTheme({
							primaryColor: settings.primaryColor,
							colorScheme: settings.colorScheme,
							direction: settings.direction,
						});

						return (
							<CssVarsProvider
								defaultColorScheme={settings.colorScheme}
								defaultMode={settings.colorScheme}
								theme={theme}>
								<CssBaseline />
								<Rtl direction={settings.direction}>
									<AppGlobalStyles />
									{children}
								</Rtl>
							</CssVarsProvider>
						);
					}}
				</SettingsConsumer>
			</SettingsProvider>
		</LocalizationProvider>
	</CacheProvider>
);

const AppGlobalStyles = () => (
	<GlobalStyles
		styles={(t) => `
			/* Remove is recharts is not used */
			.recharts-cartesian-axis-tick-value {
				color: var(--mui-palette-text-secondary);
				font-size: var(--fontSize-xs);
			}

			.recharts-cartesian-grid line {
				stroke: var(--mui-palette-divider);
			}

			.recharts-pie path:focus {
				outline: none;
			}

			/* Remove if react-simple-maps is not used */
			.rsm-geographies path:focus {
				outline: none;
			}

			.popup-content > .maplibregl-popup-content {
				padding: 0px;
			}

			/* Remove if fullcalendar is not used */
			.fc {
				--fc-bg-event-opacity: 1;
				--fc-border-color: var(--mui-palette-divider);
				--fc-daygrid-event-dot-width: 10px;
				--fc-event-bg-color: var(--mui-palette-background-paper);
				--fc-event-border-color: var(--mui-palette-divider);
				--fc-event-text-color: var(--mui-palette-primary-contrastTex);
				--fc-list-event-hover-bg-color: var(--mui-palette-background-default);
				--fc-neutral-bg-color: var(--mui-palette-background-default);
				--fc-page-bg-color: var(--mui-palette-background-default);
				--fc-today-bg-color: var(--mui-palette-background-level1);
				color: var(--mui-palette-text-primary);
			}

			.fc .fc-license-message {
				display: none;
			}

			.fc .fc-list {
				border-color: transparent;
			}

			.fc .fc-scrollgrid {
				border-color: transparent;
			}

			.fc .fc-scrollgrid-section-header > th {
				border-color: transparent;
			}

			.fc .fc-scrollgrid-section-body > td {
				border-color: transparent;
			}

			.fc .fc-col-header-cell:first-of-type {
				border-left-color: transparent;
			}

			.fc .fc-col-header-cell:last-of-type {
				border-right-color: transparent;
			}

			.fc .fc-col-header-cell-cushion {
				font-size: 0.75rem;
				font-weight: 600;
				letter-spacing: 0.5px;
				line-height: 2.5;
				padding: 8px;
				text-transform: uppercase;
			}

			.fc .fc-daygrid-day-frame {
				padding: 12px;
			}

			.fc .fc-day-other .fc-daygrid-day-top {
				color: var(--mui-palette-text-secondary);
			}

			.fc .fc-event {
				background: transparent;
				border: none;
				padding: 0;
			}

			/* Variables */
			:root {
				--fontSize-xs: 0.75rem;
				--fontSize-sm: 0.875rem;
				--fontSize-md: 1rem;
				--fontSize-lg: 1.125rem;
				--fontSize-xl: 1.25rem;
				--fontSize-2xl: 1.5rem;

				--icon-fontSize-sm: 1rem;
				--icon-fontSize-md: 1.25rem;
				--icon-fontSize-lg: 1.5rem;

				/* Remember to keep in sync with theme breakpoints */
				--maxWidth-xs: 0;
				--maxWidth-sm: 600px;
				--maxWidth-md: 900px;
				--maxWidth-lg: 1200px;
				--maxWidth-xl: 1440px;
			}

			*:focus-visible {
				outline: 2px solid var(--mui-palette-primary-main);
			}

			html {
				height: 100%;
			}

			body {
				height: 100%;
			}

			#nprogress {
				pointer-events: none;
			}

			#nprogress .bar {
				background: ${t.palette.primary.main};

				position: fixed;
				z-index: 1101;
				top: 0;
				left: 0;

				width: 100%;
				height: 2px;
			}

			/* Fancy blur effect */
			#nprogress .peg {
				display: block;
				position: absolute;
				right: 0px;
				width: 100px;
				height: 100%;
				box-shadow: 0 0 10px ${t.palette.primary.main}, 0 0 5px ${t.palette.primary.main};
				opacity: 1;

				-webkit-transform: rotate(3deg) translate(0px, -4px);
				-ms-transform: rotate(3deg) translate(0px, -4px);
				transform: rotate(3deg) translate(0px, -4px);
			}

			/* Remove these to get rid of the spinner */
			#nprogress .spinner {
				display: block;
				position: fixed;
				z-index: 1101;
				top: 15px;
				right: 15px;
			}

			#nprogress .spinner-icon {
				width: 18px;
				height: 18px;
				box-sizing: border-box;

				border: solid 2px transparent;
				border-top-color: ${t.palette.primary.main};
				border-left-color: ${t.palette.primary.main};
				border-radius: 50%;

				-webkit-animation: nprogress-spinner 400ms linear infinite;
				animation: nprogress-spinner 400ms linear infinite;
			}

			.nprogress-custom-parent {
				overflow: hidden;
				position: relative;
			}

			.nprogress-custom-parent #nprogress .spinner,
			.nprogress-custom-parent #nprogress .bar {
				position: absolute;
			}

			@-webkit-keyframes nprogress-spinner {
				0% {
					-webkit-transform: rotate(0deg);
				}
				100% {
					-webkit-transform: rotate(360deg);
				}
			}
			@keyframes nprogress-spinner {
				0% {
					transform: rotate(0deg);
				}
				100% {
					transform: rotate(360deg);
				}
			}
		`}
	/>
);
